import React, { useState } from 'react';
import { Modal, Button, Form, Alert, ListGroup, Badge } from 'react-bootstrap';

/**
 * Sanitiza defensivamente antes de mostrar/enviar series (T10 threat model).
 */
const sanitizarSerie = (s) => String(s || '')
    .replace(/[\r\n\t]/g, ' ')
    .replace(/[<>&"']/g, '')
    .slice(0, 60);

/**
 * ModalConfirmarCancelacionDependientes — pregunta al usuario si al cancelar un
 * equipo en una OT también se debe cancelar su pareja (padre o hermanos).
 * Spec: openspec/changes/items-adicionales-equipo-dependiente/specs/equipos-dependientes-sincronizacion-cancelacion.
 *
 * Props:
 *   show                boolean
 *   parejas             Array<{ equipoInOtSolicitado, parejaEnOt: [] }>
 *   onCancelarAmbos     (equiposParejaIds: string[]) => void
 *   onSoloEste          (motivoOmision: string) => void
 *   onNoCancelarNada    () => void
 */
const ModalConfirmarCancelacionDependientes = ({
    show,
    parejas = [],
    onCancelarAmbos,
    onSoloEste,
    onNoCancelarNada,
}) => {
    const [modo, setModo] = useState(null); // null | 'ambos' | 'solo'
    const [motivo, setMotivo] = useState('');

    const totalPareja = parejas.reduce((n, p) => n + (p.parejaEnOt?.length || 0), 0);
    const motivoValido = motivo.trim().length >= 5;

    const handleConfirmar = () => {
        if (modo === 'solo') {
            if (!motivoValido) return;
            onSoloEste(motivo.trim());
            setMotivo('');
            setModo(null);
            return;
        }
        // Default (o modo === 'ambos'): cancelar todo
        const equiposParejaIds = parejas.flatMap(p => (p.parejaEnOt || []).map(e => e._id));
        onCancelarAmbos(equiposParejaIds);
        setModo(null);
    };

    const handleCerrar = () => {
        setMotivo('');
        setModo(null);
        onNoCancelarNada();
    };

    return (
        <Modal show={show} onHide={handleCerrar} size="lg" backdrop="static" keyboard={false}>
            <Modal.Header closeButton={false}>
                <Modal.Title>Confirmar cancelación con pareja</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Alert variant="warning">
                    El equipo que estás cancelando tiene <strong>{totalPareja} pareja(s)</strong> en esta OT.
                    Se recomienda cancelar todo el grupo para preservar la coherencia de la calibración (ISO 17025).
                </Alert>

                {parejas.map((p, idx) => (
                    <div key={p.equipoInOtSolicitado?._id || idx} className="mb-3">
                        <div>
                            <strong>Cancelando:</strong>{' '}
                            {p.equipoInOtSolicitado?.snapshot?.item || p.equipoInOtSolicitado?.equipo?.item?.nombre}
                            {' — Serie '}
                            {sanitizarSerie(p.equipoInOtSolicitado?.snapshot?.serial || p.equipoInOtSolicitado?.equipo?.serie)}
                        </div>
                        <div className="ms-3 mt-2">
                            <strong>Pareja en esta OT:</strong>
                            <ListGroup className="mt-1">
                                {(p.parejaEnOt || []).map(pj => (
                                    <ListGroup.Item key={pj._id}>
                                        <Badge bg="info" className="me-2">🔗</Badge>
                                        {pj.snapshot?.item || pj.equipo?.item?.nombre || 'Item'}
                                        {' — Serie '}
                                        {sanitizarSerie(pj.snapshot?.serial || pj.equipo?.serie)}
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        </div>
                    </div>
                ))}

                <Form.Group className="mt-3">
                    <Form.Check
                        type="radio"
                        id="modo-ambos"
                        label="Cancelar ambos (recomendado)"
                        checked={modo === null || modo === 'ambos'}
                        onChange={() => setModo('ambos')}
                    />
                    <Form.Check
                        type="radio"
                        id="modo-solo"
                        label="Solo cancelar este equipo (requiere motivo)"
                        checked={modo === 'solo'}
                        onChange={() => setModo('solo')}
                    />
                </Form.Group>

                {modo === 'solo' && (
                    <Form.Group className="mt-3">
                        <Form.Label>Motivo de la omisión (mínimo 5 caracteres)</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={motivo}
                            onChange={(e) => setMotivo(e.target.value)}
                            placeholder="Ej: Cliente lo pidió explícitamente..."
                            isInvalid={motivo.length > 0 && !motivoValido}
                        />
                        <Form.Control.Feedback type="invalid">
                            Se requieren al menos 5 caracteres para justificar la cancelación sin pareja.
                        </Form.Control.Feedback>
                    </Form.Group>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={handleCerrar}>
                    No cancelar nada
                </Button>
                <Button
                    variant="primary"
                    onClick={handleConfirmar}
                    disabled={modo === 'solo' && !motivoValido}
                    autoFocus
                >
                    Confirmar cancelación
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ModalConfirmarCancelacionDependientes;
